// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PinInput } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { IconArrowLeft, IconDeviceMobile, IconCube, IconCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn, HoverScale } from '@/components/animations';
import { ParallaxBackground } from '../components';

export default function OtpParallax() {
  const [otp, setOtp] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleChange = (value: string) => {
    setOtp(value);
    setIsComplete(value.length === 6);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerified(true);
  };

  return (
    <ParallaxBackground preset="geometric" mouseParallax intensity={25}>
      <Center mih="100vh" p="md">
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100} duration={600}>
            <Card
              p="xl"
              radius="lg"
              style={{
                backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(16px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
              }}
            >
              {isVerified ? (
                <Stack gap="lg" align="center">
                  <FadeIn delay={100}>
                    <Center
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 20,
                        backgroundColor: 'var(--mantine-color-green-light)',
                      }}
                    >
                      <IconCheck size={40} color="var(--mantine-color-green-6)" />
                    </Center>
                  </FadeIn>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Title order={2} ta="center">Verified!</Title>
                      <Text c="dimmed" ta="center" maw={300}>
                        Your identity has been successfully verified.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <FadeIn delay={300}>
                    <HoverScale scale={1.02}>
                      <Button
                        component={Link}
                        to="/app/demo/auth-pages/login/parallax"
                        fullWidth
                        size="md"
                        radius="md"
                        variant="gradient"
                        gradient={{ from: 'pink', to: 'grape' }}
                      >
                        Continue
                      </Button>
                    </HoverScale>
                  </FadeIn>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/app/demo/auth-pages/otp"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                  >
                    Back to variants
                  </Button>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconCube size={12} />}
                        variant="gradient"
                        gradient={{ from: 'pink', to: 'grape' }}
                        size="lg"
                      >
                        Parallax Effect
                      </Badge>
                      <Center
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          backgroundColor: 'var(--mantine-color-pink-light)',
                          marginTop: 'var(--mantine-spacing-sm)',
                        }}
                      >
                        <IconDeviceMobile size={28} color="var(--mantine-color-pink-6)" />
                      </Center>
                      <Title order={2} ta="center" mt="sm">
                        Verification code
                      </Title>
                      <Text c="dimmed" size="sm" ta="center" maw={300}>
                        We've sent a 6-digit code to your phone ending in **42
                      </Text>
                    </Stack>
                  </FadeIn>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="lg">
                      <FadeIn delay={300}>
                        <Center>
                          <PinInput
                            length={6}
                            size="lg"
                            value={otp}
                            onChange={handleChange}
                            type="number"
                            oneTimeCode
                            aria-label="Verification code"
                          />
                        </Center>
                      </FadeIn>

                      <FadeIn delay={400}>
                        <HoverScale scale={1.02}>
                          <Button
                            type="submit"
                            fullWidth
                            size="md"
                            radius="md"
                            variant="gradient"
                            gradient={{ from: 'pink', to: 'grape' }}
                            disabled={!isComplete}
                          >
                            Verify code
                          </Button>
                        </HoverScale>
                      </FadeIn>
                    </Stack>
                  </form>

                  <FadeIn delay={500}>
                    <Text ta="center" size="sm" c="dimmed">
                      Didn't receive the code?{' '}
                      <Anchor component="button" type="button" fw={500}>
                        Resend
                      </Anchor>
                    </Text>
                  </FadeIn>

                  <FadeIn delay={600}>
                    <Center>
                      <Anchor component={Link} to="/app/demo/auth-pages/login/parallax" size="sm">
                        <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        Back to login
                      </Anchor>
                    </Center>
                  </FadeIn>
                </Stack>
              )}
            </Card>
          </SlideIn>
        </Container>
      </Center>
    </ParallaxBackground>
  );
}
